
import {
  IconButton,
  Toolbar,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HelpIcon from "@mui/icons-material/Help";
import { AppBar } from "../../styles/appbar";
import { useUIContext } from "../../state/ui";
import AccountMenu from "./account-menu";
import { useAuth } from '../../hooks/useAuth';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { usePreferences } from '../../hooks/usePreferences';
import BrandName from "./brandname";
import TenantInfo from "./tenant-info";
import LocationSelector from "./location-selector";
import ChecklistIcon from '@mui/icons-material/Checklist';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useNavigate } from 'react-router-dom';
import { TimeService } from '../../service/timeService';
import { useLocation as useAppLocation } from '../../hooks/useLocation';
import { useQuery, useQueryClient } from 'react-query';
import { useState } from "react";
import HelpPanel from "../help/HelpPanel";

function Appbar() {
  const { drawerOpen, setDrawerOpen } = useUIContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedLocation } = useAppLocation();
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const moduleAccess = useModuleAccess(); // Custom hook to get module access permissions
  const pendingCount = 0; // Show 0 pending - to be updated via cache invalidation when tasks are created/completed
  // Fetch active clock-in status
  const { data: statusData } = useQuery(['timeStatus'], TimeService.getMyStatus, { staleTime: 30000 });
  const activeEntry = statusData?.data?.entries?.[0] || null;
  const isClockedIn = !!activeEntry;
  const { preferences } = usePreferences();
  const isTrialPlan = preferences?.subscriptionPlan?.toLowerCase() === 'trial';

  const handleClockToggle = async () => {
    try {
      if (isClockedIn) {
        // Ask for confirmation instead of immediate clock-out
        setClockOutDialogOpen(true);
      } else {
        await TimeService.clockIn({ locationId: selectedLocation?.id || null });
      }
      queryClient.invalidateQueries(['timeStatus']);
    } catch (e) {
      // Optionally surface a toast via global error handling in httpService
      console.error(e);
    }
  };

  const confirmClockOut = async () => {
    try {
      await TimeService.clockOut();
      setClockOutDialogOpen(false);
      queryClient.invalidateQueries(['timeStatus']);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleTasksClick = () => {
    navigate('/tasks');
  };

  return (
    <>
      <AppBar position="fixed" open={drawerOpen} elevation={0}>
        <Toolbar>
          <IconButton
            onClick={handleDrawerOpen}
            sx={{
              color: "#fff",
            }}
            aria-label="open drawer"
            edge="start"
          >
            {!drawerOpen && <MenuIcon />}
          </IconButton>
          <BrandName />
          <Box sx={{ flexGrow: 1 }} />
            {
              isTrialPlan && (
                <Chip
                  icon={<WorkspacePremiumIcon sx={{ color: '#ffb74d !important', fontSize: 16 }} />}
                  label="Trial Plan"
                  size="small"
                  variant="outlined"
                  sx={{
                    mr: 2,
                    height: 26,
                    color: '#fff',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    borderColor: 'rgba(255,255,255,0.45)',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))',
                    backdropFilter: 'blur(4px)',
                    '& .MuiChip-label': {
                      px: 1.1
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.1))',
                      borderColor: 'rgba(255,255,255,0.7)'
                    }
                  }}
                />
              )
            }
          <IconButton
            onClick={handleTasksClick}
            sx={{ color: '#fff', mr: 1 }}
            title="View task list"
          >
            <Badge badgeContent={pendingCount} color="error">
              <ChecklistIcon />
            </Badge>
          </IconButton>
          {moduleAccess?.isEnabled('timeTracking') && (
            <IconButton
              onClick={handleClockToggle}
              sx={{ color: '#fff', mr: 1 }}
              title={isClockedIn ? 'Clock Out' : 'Clock In'}
            >
              <Badge
                variant="dot"
                color="warning"
                overlap="circular"
                invisible={!isClockedIn}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <AccessTimeIcon sx={{ color: '#fff' }} />
              </Badge>
            </IconButton>
          )}
          <IconButton
            onClick={() => setHelpPanelOpen(true)}
            sx={{ color: '#fff', mr: 1 }}
            title="Help & Support"
          >
            <HelpIcon />
          </IconButton>
          <LocationSelector />
          <TenantInfo />
          {user && (
            <Typography variant="body2" sx={{ color:'#fff', mr:2, maxWidth:220, textAlign:'right' }} noWrap>
              {user.firstName || user.lastName ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : user.email}<br />
              <span style={{ opacity:0.75, fontSize: '0.75rem' }}>{user.email}</span>
            </Typography>
          )}
          {/* comment out for now <UserNotifications />
          <UserMessages /> */}
          <AccountMenu />
        </Toolbar>
      </AppBar>
      <Dialog open={clockOutDialogOpen} onClose={() => setClockOutDialogOpen(false)}>
        <DialogTitle>Clock Out</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {activeEntry?.clockInAt
              ? `You clocked in at ${new Date(activeEntry.clockInAt).toLocaleString()}.`
              : 'You are currently clocked in.'}
            {` Do you want to clock out now?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockOutDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmClockOut} variant="contained" color="primary">Clock Out</Button>
        </DialogActions>
      </Dialog>
      <HelpPanel open={helpPanelOpen} onClose={() => setHelpPanelOpen(false)} />
    </>
  );
}

export default Appbar;
